/**
 * Create/Edit SIM page that orchestrates a multi-step wizard for building
 * simulation records (basic info, avatars, levels, keywords).
 *
 * @module pages/sim/CreateSims
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {Redirect, useLocation} from 'react-router-dom';
import { connect } from 'react-redux';
import SelectAvatars from '../../components/sim/selectAvatars/SelectAvatars.jsx';
import BasicInfo from '../../components/sim/BasicInfo.jsx';
import Keywords from '../../components/sim/keywords/Keywords.jsx';
import CreateLevels from '../../components/sim/createLevels/CreateLevels.jsx';
import {fetchSimDetails} from "../../redux/sims/actions";

/**
 * Component function for managing the creation of simulations based on a multi-step process.
 *
 * This function determines which step of the simulation creation process to render based on the `activeStep`
 * property of the `newSim` object, and handles updates to simulation details dynamically through hooks like
 * `useEffect` and state management with `useState`.
 *
 * It utilizes the following steps:
 * - Step 0: Displays basic information input (BasicInfo component).
 * - Step 1: Allows users to select avatars (SelectAvatars component).
 * - Step 2: Facilitates the creation of levels (CreateLevels component).
 * - Step 3: Displays keyword setup (Keywords component) based on conditions involving the `simProductType`.
 * - Any other scenario results in a redirection to the main simulations page.
 *
 * @param {Object} params - The parameter object for the simulation creation.
 * @param {Object} params.newSim - Object representing the current simulation being created.
 * @param {number} params.newSim.activeStep - Indicates the active step in the simulation creation process.
 * @param {string} [params.newSim.product_id] - ID of the simulation product being created (optional).
 * @param {Array<string>} simProductType - List of simulation product types for determining step logic.
 * @param {Function} fetchSimDetails - Function to fetch the simulation details by ID.
 * @returns {JSX.Element} The JSX to render for the active step or redirect when the conditions are met.
 */
const CreateSims = ({
    newSim,
    simProductType,
    fetchSimDetails
}) => {
    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const [simId, setSimId] = React.useState(null);

    useEffect(
        () => {
            const getSim = async () => {
                fetchSimDetails(simId);
            }

            if (simId) {
                getSim();
            }
        },
        [simId, fetchSimDetails]
    )

    useEffect(
        () => {
            if (newSim && (newSim.id !== simId)) {
                setSimId(urlParams.get('simId'));
            }
        },
        [newSim, simId, urlParams]
    )

    if (urlParams.get('simId') && !simId) {
        setSimId(urlParams.get('simId'));
    }

    if (newSim.activeStep === 0) {
        return (
            <div>
                <BasicInfo />
            </div>
        );
    }
    if (newSim.activeStep === 1) {
        return (
            <div>
                <SelectAvatars />
            </div>
        );
    }
    if (newSim.activeStep === 2) {
        return (
            <div>
                <CreateLevels />
            </div>
        );
    }
    //if (newSim.activeStep === 3 && !simProductType.includes('Voice Only')) {
    //if ((newSim.activeStep === 3) && (newSim.product_id !== "5fd9e04bbddbcc04f024f09d")) {
    if (newSim.activeStep === 3 && simProductType.includes('Internal Data')) {
        return (
            <div>
                <Keywords />
            </div>
        );
    }

    return (
        <div>
            {/* redirect to sims page */}
            <Redirect to="/sims" />
        </div>
    );
};

CreateSims.propTypes = {
    newSim: PropTypes.object,
    simProductType: PropTypes.string,
    fetchSimDetails: PropTypes.func,
};

/**
 * Maps Redux state to the CreateSims component props.
 *
 * @param {Object} state - The Redux store state.
 * @returns {Object} Props derived from state.
 */
const mapStateToProps = (state) => ({
    newSim: state.sim.newSim,
    simProductType: state.sim.currentSimProductType,
});

/**
 * Maps Redux dispatch to the CreateSims component props.
 *
 * @type {Object}
 */
const mapDispatchToProps = {
    fetchSimDetails,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateSims);
